import { useState, useRef, useEffect } from "react";
import { Info } from "lucide-react";

/**
 * Generic "what is this / how is it calculated" tooltip. Hover or click the
 * info icon to reveal a short explanation. Unlike AutoTip (which is specific
 * to auto-calculated form fields), this is a plain explainer for any label,
 * stat box, or heading that benefits from a quick definition.
 *
 * Props:
 *   title    – short heading shown in the popover (e.g. the box's label)
 *   children – explanation body (string or JSX; line breaks in a string
 *              render as paragraph breaks)
 *   side     – "right" (default) or "left" — which edge the popover opens from
 */
export default function InfoTip({ title, children, side = "right", className = "" }) {
    const [open, setOpen] = useState(false);
    const wrapRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        const onClickOutside = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onClickOutside);
        return () => document.removeEventListener("mousedown", onClickOutside);
    }, [open]);

    return (
        <span
            ref={wrapRef}
            className={`relative inline-flex items-center align-middle ml-1 ${className}`}
            onMouseEnter={() => setOpen(true)}
            onMouseLeave={() => setOpen(false)}
        >
            <button
                type="button"
                onClick={(e) => { e.preventDefault(); setOpen((v) => !v); }}
                className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full text-slate-400 hover:text-slate-600 transition-colors"
                title={title || "More info"}
                data-testid="info-tip-icon"
                aria-label={title ? `About ${title}` : "More info"}
            >
                <Info className="w-3 h-3" />
            </button>

            {open && (
                <span
                    role="tooltip"
                    className={`absolute z-50 top-full mt-1.5 w-64 rounded-md border border-slate-300 bg-white shadow-lg p-3 text-[11px] text-slate-700 leading-relaxed ${
                        side === "left" ? "right-0" : "left-0"
                    }`}
                    data-testid="info-tip-tooltip"
                >
                    {title && (
                        <div className="font-semibold text-slate-800 mb-1 text-[11.5px]">{title}</div>
                    )}
                    <div className="whitespace-pre-line">{children}</div>
                </span>
            )}
        </span>
    );
}
